
import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDataStore } from 'files/StateManagement.js';

import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';

const AnimatedElement = ({ animateIn, animateOut, animateWithPage = false, children, config, delay = 0.5, useContentDivider = false, persist = true, style, visible }) => {

    const action = useRef(null);
    const target = useRef(null);

    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 12, ...config },
        ...animateOut
    }));
    const [mobileAnimations, setMobileAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 12, ...config },
        opacity: 0,
        transform: 'scale(0.5)'
    }));
    const { system } = useDataStore();
    const [visiblePrivate, setVisiblePrivate] = useState(false);

    const onSetAnimations = () => {

        // set internal visibility flag to true if applicable
        if(action.current === 'show') {
            setVisiblePrivate(true);
        }

        // update animation values with visible based timeout
        setAnimations({
            to: async next => {

                // wait using optional delay if provided
                await Utils.sleep(delay || 0);

                // run animation
                await next(action.current === 'show' ? animateIn : animateOut);

                // set internal visibility flag to false after animation has completed if applicable
                if(visible === 'hide') {
                    setVisiblePrivate(false);
                }
            }
        });

        // animate mobile divider into view if applicable
        if(true || Utils.isMobile() === true) {
            setMobileAnimations({
                to: async next => { 

                    // wait using optional delay if provided
                    await Utils.sleep(delay || 0);

                    // run animation
                    await next({
                        opacity: action.current === 'show' ? 1 : 0,
                        transform: action.current === 'show' ? 'scale(1)' : 'scale(0.5)' 
                    });
                }
            });
        }
    }

    useEffect(() => {

        // run standard animations if animateWithPage is false
        if(animateWithPage === false) {
            action.current = visible ? 'show' : 'hide';
            onSetAnimations();
            return;
        }

        // set interval and wait for ref to post
        let interval = setInterval(() => {

            // continue interval if no ref has posted
            if(!target.current) {
                return;
            }

            // clear interval
            clearInterval(interval);
            
            // register observer and intersection logic
            let observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if(entry.intersectionRatio > 0) {
                        action.current = 'show';
                        onSetAnimations();
                    } else if(persist === false) {
                        action.current = 'hide';
                        onSetAnimations();
                    }
                });
            }, {
                rootMargin: '0px'
            });

            // add observer to component
            observer.observe(target.current);

            return () => {
                clearInterval(interval);
                observer.disconnect();
            }
        }, 100);
        
    }, [animateWithPage, visible]);

    return (
        <div 
        ref={target}
        style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: visiblePrivate ? null : 500,
            maxWidth: '100%',
            width: '100%'
        }}>
            {visiblePrivate === true && (
                <>
                <animated.div style={{
                    ...animations,
                    position: 'relative',
                    width: '100%',
                    ...style
                }}>
                    {children}
                </animated.div>
                {useContentDivider === true && (
                    <animated.img 
                    src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                    style={{
                        alignSelf: 'center',
                        height: 'auto',
                        marginBottom: Appearance.styles.panelSpacing(),
                        marginTop: Appearance.styles.panelSpacing(),
                        maxHeight: 25,
                        objectFit: 'contain',
                        width: '100%',
                        ...mobileAnimations,
                        ...system.window.isMobile && {
                            maxWidth: '75%'
                        }
                    }} />
                )}  
                </> 
            )}
        </div>
    )
}

export default AnimatedElement;